import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Button,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  InputAdornment,
  Skeleton,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  LinearProgress,
  Badge,
  Tooltip,
} from "@mui/material";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TimelineIcon from "@mui/icons-material/Timeline";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ChecklistIcon from "@mui/icons-material/Checklist";
import Swal from "sweetalert2";
import { api } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";

const computeDue = (s) => {
  if (!s.due_date) return { badge: "—", color: "default" };
  const today = new Date();
  const due = new Date(s.due_date);
  const d0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const d1 = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const diff = Math.ceil((d1 - d0) / 86400000);

  if (diff > 3) return { badge: `${diff}d left`, color: "success" };
  if (diff > 0) return { badge: `${diff}d left`, color: "warning" };
  if (diff === 0) return { badge: "Due Today", color: "warning" };
  return { badge: `${Math.abs(diff)}d overdue`, color: "error" };
};

export default function Desk() {
  const { user, can } = useAuth();
  const myId = user?.id;
  const myRole = user?.role;

  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const [timeline, setTimeline] = useState([]);
  const [timelineOpen, setTimelineOpen] = useState(false);

  const [submitModal, setSubmitModal] = useState({
    open: false,
    jobServiceId: null,
    remark: "",
    cost: "",
    nextStage: "",
  });

  const [assignModal, setAssignModal] = useState({
    open: false,
    jobServiceId: null,
    teamUserId: "",
  });

  const [checklistModal, setChecklistModal] = useState({
    open: false,
    jobServiceId: null,
    serviceName: "",
    items: [],
    summary: { total: 0, done: 0, pending: 0, required_pending: 0, all_required_done: true },
    loading: false,
  });

  const loadDesk = async () => {
    setLoading(true);
    try {
      const [deskRes, userRes] = await Promise.all([
        api.get(`/services/desk.php`),
        api.get(`/users/list.php`),
      ]);
      setServices(deskRes.services || []);
      setUsers(userRes.users || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDesk();
  }, []);

  const usersByRole = (role) => users.filter((u) => u.role === role);

  const loadTimeline = async (id) => {
    try {
      const res = await api.get(`/jobs/timeline.php?job_service_id=${id}`);
      setTimeline(res.timeline || []);
      setTimelineOpen(true);
    } catch (e) {
      console.error(e);
    }
  };

  const doSubmit = async () => {
    const { jobServiceId, remark, cost, nextStage } = submitModal;
    if (!remark.trim()) {
      setMsg({ type: "warning", text: "Remark is required" });
      return;
    }
    try {
      await api.post("/services/desk_submit.php", {
        job_service_id: jobServiceId,
        remark,
        cost,
        next_stage: nextStage,
      });
      setSubmitModal({
        open: false,
        jobServiceId: null,
        remark: "",
        cost: "",
        nextStage: "",
      });
      setMsg({ type: "success", text: `Submitted to ${nextStage}` });
      loadDesk();
    } catch (e) {
      // Server may reject with checklist pending - show rich error
      if (e.message && e.message.toLowerCase().includes("checklist")) {
        Swal.fire({
          icon: "warning",
          title: "Checklist Incomplete",
          text: "Please complete all required checklist items before submitting.",
        });
      } else {
        setMsg({ type: "error", text: e.message || "Submit failed" });
      }
    }
  };

  const openChecklist = async (jobServiceId) => {
    setChecklistModal({
      open: true,
      jobServiceId,
      serviceName: "",
      items: [],
      summary: { total: 0, done: 0, pending: 0, required_pending: 0, all_required_done: true },
      loading: true,
    });
    try {
      const res = await api.get(`/desk/checklist.php?job_service_id=${jobServiceId}`);
      setChecklistModal({
        open: true,
        jobServiceId,
        serviceName: res.service_name || "",
        items: res.items || [],
        summary: res.summary || { total: 0, done: 0, pending: 0, required_pending: 0, all_required_done: true },
        loading: false,
      });
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Failed to load checklist" });
      setChecklistModal((p) => ({ ...p, open: false, loading: false }));
    }
  };

  const toggleChecklistItem = async (templateId, isDone) => {
    try {
      await api.post("/desk/checklist_toggle.php", {
        job_service_id: checklistModal.jobServiceId,
        template_id: templateId,
        is_done: isDone ? 1 : 0,
      });
      // Refresh from server to get accurate summary
      const res = await api.get(
        `/desk/checklist.php?job_service_id=${checklistModal.jobServiceId}`
      );
      setChecklistModal((p) => ({
        ...p,
        items: res.items || [],
        summary: res.summary || p.summary,
      }));
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Toggle failed" });
    }
  };

  const doAssignTeam = async () => {
    if (!assignModal.teamUserId) {
      setMsg({ type: "warning", text: "Select team member" });
      return;
    }
    try {
      await api.post("/services/desk_assign_team.php", {
        job_service_id: assignModal.jobServiceId,
        team_user_id: assignModal.teamUserId,
      });
      setAssignModal({ open: false, jobServiceId: null, teamUserId: "" });
      setMsg({ type: "success", text: "Team assigned" });
      loadDesk();
    } catch (e) {
      setMsg({ type: "error", text: e.message || "Assign failed" });
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
          <DesktopWindowsIcon />
        </Avatar>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            My Desk
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Active work files assigned to you
          </Typography>
        </Box>
      </Stack>

      {msg && (
        <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      <Paper sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Work #</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>Due</TableCell>
              <TableCell>Team</TableCell>
              <TableCell>Manager</TableCell>
              <TableCell>Auditor</TableCell>
              <TableCell align="center" sx={{ minWidth: 200 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              [...Array(5)].map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  <TableCell colSpan={8}>
                    <Skeleton height={40} />
                  </TableCell>
                </TableRow>
              ))}

            {!loading && services.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No files found on your desk
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              services.map((s) => {
                const due = computeDue(s);
                const isManagerOwner =
                  myRole === "Manager" &&
                  s.manager_user_id == myId &&
                  s.current_stage === "Manager";
                const isTeamOwner =
                  myRole === "Team" &&
                  s.current_stage === "Team" &&
                  s.current_user_id == myId;
                const isAuditorOwner =
                  myRole === "Auditor" &&
                  s.current_stage === "Auditor" &&
                  s.current_user_id == myId;

                return (
                  <TableRow key={s.id} hover>
                    <TableCell>#{s.id}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {s.client_name}
                    </TableCell>
                    <TableCell>{s.service_name}</TableCell>
                    <TableCell>
                      <Chip size="small" label={due.badge} color={due.color} />
                    </TableCell>
                    <TableCell>
                      {s.team_user_name ? (
                        <Typography variant="caption">
                          {s.team_user_name}
                        </Typography>
                      ) : isManagerOwner && can("desk.assign_team") ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<GroupAddIcon />}
                          onClick={() =>
                            setAssignModal({
                              open: true,
                              jobServiceId: s.id,
                              teamUserId: "",
                            })
                          }
                        >
                          Assign
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {s.manager_user_name || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {s.audit_required == 1
                          ? s.auditor_user_name || "—"
                          : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                        flexWrap="wrap"
                      >
                        {can("desk.checklist") && (
                          <Tooltip title="Checklist">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => openChecklist(s.id)}
                            >
                              <ChecklistIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          title="Timeline"
                          onClick={() => loadTimeline(s.id)}
                        >
                          <TimelineIcon fontSize="small" />
                        </IconButton>

                        {isTeamOwner && can("desk.submit") && (
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<SendIcon />}
                            onClick={() =>
                              setSubmitModal({
                                open: true,
                                jobServiceId: s.id,
                                remark: "",
                                cost: "",
                                nextStage: "Manager",
                              })
                            }
                          >
                            Manager
                          </Button>
                        )}

                        {isManagerOwner && s.team_user_id && can("desk.submit") && (
                          <Button
                            size="small"
                            variant="contained"
                            color={
                              s.audit_required == 1 ? "secondary" : "success"
                            }
                            startIcon={<SendIcon />}
                            onClick={() =>
                              setSubmitModal({
                                open: true,
                                jobServiceId: s.id,
                                remark: "",
                                cost: "",
                                nextStage:
                                  s.audit_required == 1 ? "Auditor" : "BDM",
                              })
                            }
                          >
                            {s.audit_required == 1 ? "Auditor" : "BDM"}
                          </Button>
                        )}

                        {isAuditorOwner && can("desk.submit") && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() =>
                                setSubmitModal({
                                  open: true,
                                  jobServiceId: s.id,
                                  remark: "",
                                  cost: "",
                                  nextStage: "BDM",
                                })
                              }
                            >
                              Verify
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={() =>
                                setSubmitModal({
                                  open: true,
                                  jobServiceId: s.id,
                                  remark: "",
                                  cost: "",
                                  nextStage: "Manager",
                                })
                              }
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </Paper>

      {/* SUBMIT DIALOG */}
      <Dialog
        open={submitModal.open}
        onClose={() => setSubmitModal((s) => ({ ...s, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Submit Work → {submitModal.nextStage}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Remark *"
              value={submitModal.remark}
              onChange={(e) =>
                setSubmitModal((p) => ({ ...p, remark: e.target.value }))
              }
            />
            <TextField
              fullWidth
              type="number"
              label="Out of Pocket Cost"
              value={submitModal.cost}
              onChange={(e) =>
                setSubmitModal((p) => ({ ...p, cost: e.target.value }))
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSubmitModal((s) => ({ ...s, open: false }))}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={doSubmit}>
            Submit to {submitModal.nextStage}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ASSIGN TEAM DIALOG */}
      <Dialog
        open={assignModal.open}
        onClose={() => setAssignModal((s) => ({ ...s, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Assign Team Member</DialogTitle>
        <DialogContent dividers>
          <TextField
            select
            fullWidth
            label="Team Member"
            value={assignModal.teamUserId}
            onChange={(e) =>
              setAssignModal((p) => ({ ...p, teamUserId: e.target.value }))
            }
            sx={{ mt: 1 }}
          >
            <MenuItem value="">Select</MenuItem>
            {usersByRole("Team").map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAssignModal((s) => ({ ...s, open: false }))}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={doAssignTeam}>
            Assign Team
          </Button>
        </DialogActions>
      </Dialog>

      {/* CHECKLIST DIALOG */}
      <Dialog
        open={checklistModal.open}
        onClose={() => setChecklistModal((p) => ({ ...p, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ChecklistIcon color="info" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Checklist
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {checklistModal.serviceName}
              </Typography>
            </Box>
            <Badge
              badgeContent={`${checklistModal.summary.done}/${checklistModal.summary.total}`}
              color="primary"
              max={999}
              sx={{ "& .MuiBadge-badge": { right: -20, top: 10 } }}
            />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {checklistModal.loading ? (
            <Stack spacing={1}>
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} height={40} />
              ))}
            </Stack>
          ) : checklistModal.items.length === 0 ? (
            <Alert severity="info">
              No checklist items defined for this service.
            </Alert>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={
                    checklistModal.summary.total > 0
                      ? (checklistModal.summary.done / checklistModal.summary.total) * 100
                      : 0
                  }
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Chip
                    size="small"
                    label={`Done ${checklistModal.summary.done}`}
                    color="success"
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={`Pending ${checklistModal.summary.pending}`}
                    color="warning"
                    variant="outlined"
                  />
                  {checklistModal.summary.required_pending > 0 && (
                    <Chip
                      size="small"
                      label={`Required Pending ${checklistModal.summary.required_pending}`}
                      color="error"
                    />
                  )}
                </Stack>
              </Box>
              <List dense>
                {checklistModal.items.map((it) => {
                  const done = Number(it.is_done) === 1;
                  return (
                    <ListItem
                      key={it.template_id}
                      sx={{
                        border: 1,
                        borderColor: done ? "success.light" : "divider",
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: done ? "success.lighter" : "background.paper",
                      }}
                      secondaryAction={
                        it.is_required == 1 && (
                          <Chip size="small" label="Required" color="error" variant="outlined" />
                        )
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={done}
                          onChange={(e) =>
                            toggleChecklistItem(it.template_id, e.target.checked)
                          }
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              textDecoration: done ? "line-through" : "none",
                              color: done ? "text.secondary" : "text.primary",
                            }}
                          >
                            {it.item_text}
                          </Typography>
                        }
                        secondary={
                          done && it.done_by_name ? (
                            <Typography variant="caption" color="text.disabled">
                              ✓ by {it.done_by_name} • {it.done_at}
                            </Typography>
                          ) : null
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
              {!checklistModal.summary.all_required_done && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Complete all required items before submitting this work.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChecklistModal((p) => ({ ...p, open: false }))}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* TIMELINE DIALOG */}
      <Dialog
        open={timelineOpen}
        onClose={() => setTimelineOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Service Timeline</DialogTitle>
        <DialogContent dividers>
          {timeline.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No timeline found
            </Typography>
          ) : (
            <List dense>
              {timeline.map((t) => (
                <ListItem
                  key={t.id}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t.action_type} by {t.from_user || "System"}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography variant="caption" color="text.disabled">
                          {t.created_at}
                        </Typography>
                        {t.remark && (
                          <Typography variant="caption" display="block">
                            💬 {t.remark}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimelineOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
